import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { ClassRoom, Student, TimetableSlot, VacationSlot, VacationTerm } from '@/lib/types';
import { useTeachers } from '@/hooks/useTeachers';
import { useProfile } from '@/hooks/useProfile';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import VacationWeeklyTimetable from '@/pages/vacation-timetable/components/VacationWeeklyTimetable';
import VacationSlotFormModal from '@/pages/vacation-timetable/components/VacationSlotFormModal';
import VacationTermStartModal from '@/pages/vacation-timetable/components/VacationTermStartModal';
import VacationPastTermList from '@/pages/vacation-timetable/components/VacationPastTermList';
import {
  buildVacationGroups,
  buildVacationPrintDays,
  type ResolvedSlot,
} from '@/pages/vacation-timetable/utils';
import { printWeeklyTimetable } from '@/pages/timetable/printTimetable';

const INTEGRATED_VIEW = 'all';

type StartSource = 'empty' | 'regular' | 'copy';

export default function VacationTimetable() {
  const [terms, setTerms] = useState<VacationTerm[]>([]);
  const [slots, setSlots] = useState<VacationSlot[]>([]);
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [classStudentIds, setClassStudentIds] = useState<Record<string, string[]>>({});
  const [slotStudentIds, setSlotStudentIds] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState<string>('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VacationSlot | null>(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [ending, setEnding] = useState(false);
  const [termToDelete, setTermToDelete] = useState<VacationTerm | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 방학특강 시작 모달
  const [startOpen, setStartOpen] = useState(false);
  const [startName, setStartName] = useState('');
  const [startSource, setStartSource] = useState<StartSource>('regular');
  const [startCopyId, setStartCopyId] = useState('');

  const { teachers } = useTeachers();
  const { profile } = useProfile();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [termsRes, classesRes, studentsRes, classStudentsRes, slotStudentsRes] =
        await Promise.all([
          supabase.from('vacation_terms').select('*').order('started_at', { ascending: false }),
          supabase.from('classes').select('*'),
          supabase.from('students').select('*').order('name', { ascending: true }),
          supabase.from('class_students').select('class_id, student_id'),
          supabase.from('vacation_timetable_students').select('slot_id, student_id'),
        ]);
      if (termsRes.error) throw termsRes.error;
      if (classesRes.error) throw classesRes.error;
      if (studentsRes.error) throw studentsRes.error;
      if (classStudentsRes.error) throw classStudentsRes.error;
      if (slotStudentsRes.error) throw slotStudentsRes.error;

      const termList = (termsRes.data as VacationTerm[]) || [];
      setTerms(termList);
      setClasses((classesRes.data as ClassRoom[]) || []);
      setStudents((studentsRes.data as Student[]) || []);

      const classIds: Record<string, string[]> = {};
      ((classStudentsRes.data as Array<{ class_id: string; student_id: string }>) || []).forEach(
        (row) => {
          if (!classIds[row.class_id]) classIds[row.class_id] = [];
          classIds[row.class_id].push(row.student_id);
        }
      );
      setClassStudentIds(classIds);

      const slotIds: Record<string, string[]> = {};
      ((slotStudentsRes.data as Array<{ slot_id: string; student_id: string }>) || []).forEach(
        (row) => {
          if (!slotIds[row.slot_id]) slotIds[row.slot_id] = [];
          slotIds[row.slot_id].push(row.student_id);
        }
      );
      setSlotStudentIds(slotIds);

      // 운영 중인 방학특강의 시간표만 불러와요.
      const active = termList.find((t) => t.status === 'active') || null;
      if (active) {
        const { data, error: slotsError } = await supabase
          .from('vacation_timetable')
          .select('*')
          .eq('term_id', active.id)
          .order('day_of_week', { ascending: true })
          .order('start_time', { ascending: true });
        if (slotsError) throw slotsError;
        setSlots((data as VacationSlot[]) || []);
      } else {
        setSlots([]);
      }
    } catch (err) {
      setError('방학특강 시간표를 불러오지 못했어요.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeTerm = useMemo(() => terms.find((t) => t.status === 'active') || null, [terms]);
  const pastTerms = useMemo(() => terms.filter((t) => t.status === 'ended'), [terms]);

  const teacherMap = useMemo(
    () => new Map(teachers.map((t) => [t.id, t.full_name || '이름 없음'])),
    [teachers]
  );
  const classMap = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);
  const studentMap = useMemo(() => new Map(students.map((s) => [s.id, s.name])), [students]);

  const namesByIds = useCallback(
    (ids: string[]) => ids.map((id) => studentMap.get(id) || '').filter(Boolean),
    [studentMap]
  );

  const classRosterNames = useMemo(() => {
    const out: Record<string, string[]> = {};
    Object.entries(classStudentIds).forEach(([classId, ids]) => {
      out[classId] = namesByIds(ids);
    });
    return out;
  }, [classStudentIds, namesByIds]);

  const slotRosterNames = useMemo(() => {
    const out: Record<string, string[]> = {};
    Object.entries(slotStudentIds).forEach(([slotId, ids]) => {
      out[slotId] = namesByIds(ids);
    });
    return out;
  }, [slotStudentIds, namesByIds]);

  const groups = useMemo(
    () => buildVacationGroups(slots, classMap, teacherMap, classRosterNames, slotRosterNames),
    [slots, classMap, teacherMap, classRosterNames, slotRosterNames]
  );

  const activeView = selected || groups[0]?.id || INTEGRATED_VIEW;
  const isIntegrated = activeView === INTEGRATED_VIEW;
  const activeGroup = groups.find((g) => g.id === activeView) || null;

  const viewItems: ResolvedSlot[] = useMemo(
    () => (isIntegrated ? groups.flatMap((g) => g.items) : activeGroup?.items || []),
    [isIntegrated, groups, activeGroup]
  );

  function openStartNew() {
    setStartName('');
    setStartSource(pastTerms.length > 0 ? 'copy' : 'regular');
    setStartCopyId(pastTerms[0]?.id || '');
    setStartOpen(true);
  }

  function openStartReuse(term: VacationTerm) {
    setStartName(term.name);
    setStartSource('copy');
    setStartCopyId(term.id);
    setStartOpen(true);
  }

  // 정규 시간표를 그대로 방학특강으로 복사해요. (운영 중인 방학특강 항목만 교체)
  async function handleImport() {
    if (!activeTerm) return;
    setImporting(true);
    setError('');
    setNotice('');
    try {
      const { data, error: fetchError } = await supabase
        .from('timetable')
        .select('*')
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });
      if (fetchError) throw fetchError;

      const regular = (data as TimetableSlot[]) || [];
      if (regular.length === 0) {
        setNotice('정규 시간표에 등록된 일정이 없어요. 먼저 정규 시간표를 만들어 주세요.');
        return;
      }

      const { error: deleteError } = await supabase
        .from('vacation_timetable')
        .delete()
        .eq('term_id', activeTerm.id);
      if (deleteError) throw deleteError;

      const payload = regular.map((s) => ({
        academy_id: profile?.academy_id || null,
        term_id: activeTerm.id,
        class_id: s.class_id,
        name: null,
        subject: null,
        teacher_id: null,
        day_of_week: s.day_of_week,
        start_time: s.start_time,
        end_time: s.end_time,
      }));
      const { error: insertError } = await supabase.from('vacation_timetable').insert(payload);
      if (insertError) throw insertError;

      setNotice(
        `정규 시간표 ${regular.length}개 일정을 방학특강으로 복사했어요. 이제 필요에 따라 수정하세요.`
      );
      setSelected(INTEGRATED_VIEW);
      await loadData();
    } catch (err) {
      setError('정규 시간표를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setImporting(false);
      setConfirmImport(false);
    }
  }

  // 방학특강 종료: 출결이 다시 정규 시간표 기준으로 돌아가요.
  async function handleEnd() {
    if (!activeTerm) return;
    setEnding(true);
    setError('');
    try {
      const { error: endError } = await supabase
        .from('vacation_terms')
        .update({ status: 'ended', ended_at: new Date().toISOString() })
        .eq('id', activeTerm.id);
      if (endError) throw endError;
      setNotice(
        `“${activeTerm.name}” 방학특강을 종료했어요. 출결이 다시 정규 시간표 기준으로 동작합니다.`
      );
      await loadData();
    } catch (err) {
      setError('방학특강을 종료하지 못했어요. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setEnding(false);
      setConfirmEnd(false);
    }
  }

  async function handleDeleteTerm() {
    if (!termToDelete) return;
    setDeleting(true);
    setError('');
    try {
      const { error: delError } = await supabase
        .from('vacation_terms')
        .delete()
        .eq('id', termToDelete.id);
      if (delError) throw delError;
      setNotice(`“${termToDelete.name}” 기록을 삭제했어요.`);
      setTermToDelete(null);
      await loadData();
    } catch (err) {
      setError('방학특강 기록을 삭제하지 못했어요.');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  }

  function handlePrint() {
    const title = isIntegrated
      ? '방학특강 통합 시간표'
      : `${activeGroup?.name ?? ''} 선생님 방학특강 시간표`;
    printWeeklyTimetable({
      title,
      subtitle: profile?.academy_name || '',
      days: buildVacationPrintDays(viewItems, isIntegrated),
    });
  }

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(item: ResolvedSlot) {
    setEditing(item.raw);
    setFormOpen(true);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">방학특강 시간표</h2>
          <p className="mt-1 text-sm text-foreground-500">
            방학특강을 시작하면 출결이 방학특강 시간표 기준으로 동작하고, 종료하면 정규 시간표로
            돌아가요.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeTerm ? (
            <button
              onClick={() => setConfirmEnd(true)}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-stop-circle-line text-base"></i>
              방학특강 종료
            </button>
          ) : (
            <button
              onClick={openStartNew}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-base"></i>
              방학특강 시작
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {notice && (
        <div className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
          <i className="ri-information-line"></i>
          <span>{notice}</span>
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
        </div>
      ) : activeTerm ? (
        <>
          {/* 운영 중 배너 */}
          <div className="flex flex-col gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-snowy-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-bold text-foreground-950">
                  운영 중: {activeTerm.name}
                </p>
                <p className="text-xs text-foreground-600">
                  지금 출결은 이 방학특강 시간표 기준으로 기록돼요.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500"></span>
              운영 중
            </span>
          </div>

          {/* 도구 모음 */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setConfirmImport(true)}
              disabled={importing}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-file-copy-line text-base"></i>
              정규 시간표 불러오기
            </button>
            <button
              onClick={openAdd}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
            >
              <i className="ri-add-line text-base"></i>
              항목 추가
            </button>
            <button
              onClick={handlePrint}
              disabled={viewItems.length === 0}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-printer-line text-base"></i>
              인쇄하기
            </button>
          </div>

          {slots.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-white py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-snowy-line text-2xl"></i>
              </div>
              <p className="mt-4 text-sm font-medium text-foreground-700">
                아직 방학특강 시간표가 비어 있어요.
              </p>
              <p className="mt-1 text-xs text-foreground-400">
                위의 “정규 시간표 불러오기”로 시작하거나 “항목 추가”로 직접 만들어 보세요.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {groups.map((g) => {
                  const active = !isIntegrated && g.id === activeView;
                  return (
                    <button
                      key={g.id}
                      onClick={() => setSelected(g.id)}
                      className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      <i className="ri-user-line text-xs"></i>
                      {g.name}
                      <span
                        className={`rounded-full px-1.5 text-[10px] leading-4 ${
                          active
                            ? 'bg-background-50/25 text-background-50'
                            : 'bg-white text-foreground-500'
                        }`}
                      >
                        {g.items.length}
                      </span>
                    </button>
                  );
                })}
                <button
                  onClick={() => setSelected(INTEGRATED_VIEW)}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                    isIntegrated
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-accent-100 text-accent-800 hover:bg-accent-200'
                  }`}
                >
                  <i className="ri-stack-line text-xs"></i>
                  통합 시간표
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                <i className="ri-information-line text-secondary-500"></i>
                {isIntegrated ? (
                  <span>
                    모든 선생님의 방학특강을 합쳐서 보여줘요. 각 수업에 담당 선생님이 표시돼요.
                  </span>
                ) : (
                  <span>
                    <strong className="font-semibold text-foreground-900">
                      {activeGroup?.name}
                    </strong>{' '}
                    선생님의 방학특강 시간표예요. 총 {viewItems.length}개의 수업이 있어요.
                  </span>
                )}
                <span className="text-foreground-400">
                  수업 카드를 누르면 반 상세로 이동하고, 연필 아이콘을 누르면 이 특강의 시간·학생 명단을 바로 수정할 수 있어요.
                </span>
              </div>

              <VacationWeeklyTimetable
                items={viewItems}
                showTeacher={isIntegrated}
                onEdit={openEdit}
              />
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-white py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-snowy-line text-2xl"></i>
          </div>
          <p className="mt-4 text-sm font-medium text-foreground-700">
            지금은 운영 중인 방학특강이 없어요.
          </p>
          <p className="mt-1 text-xs text-foreground-400">
            출결은 정규 시간표 기준으로 동작하고 있어요. 방학특강을 시작하면 이 시간표 기준으로
            바뀝니다.
          </p>
          <button
            onClick={openStartNew}
            className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-play-circle-line"></i> 방학특강 시작
          </button>
        </div>
      )}

      <VacationPastTermList
        terms={pastTerms}
        onReuse={openStartReuse}
        onDelete={(t) => setTermToDelete(t)}
      />

      <VacationSlotFormModal
        open={formOpen}
        slot={editing}
        classes={classes}
        teachers={teachers}
        students={students}
        classStudentIdsByClass={classStudentIds}
        slotStudentIds={editing ? slotStudentIds[editing.id] || [] : []}
        academyId={profile?.academy_id || null}
        termId={activeTerm?.id || null}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          loadData();
        }}
        onDeleted={() => {
          setFormOpen(false);
          loadData();
        }}
      />

      <VacationTermStartModal
        open={startOpen}
        academyId={profile?.academy_id || null}
        pastTerms={pastTerms}
        defaultName={startName}
        defaultSource={startSource}
        defaultCopyTermId={startCopyId}
        onClose={() => setStartOpen(false)}
        onStarted={() => {
          setStartOpen(false);
          setSelected(INTEGRATED_VIEW);
          setNotice('방학특강을 시작했어요. 이제 출결이 방학특강 기준으로 동작합니다.');
          loadData();
        }}
      />

      <ConfirmDialog
        open={confirmImport}
        title="정규 시간표 불러오기"
        message="정규 시간표를 방학특강으로 복사해요. 운영 중인 방학특강 항목이 모두 지워지고 정규 시간표로 새로 채워집니다. 계속할까요?"
        confirmLabel="불러오기"
        loading={importing}
        onCancel={() => setConfirmImport(false)}
        onConfirm={handleImport}
      />

      <ConfirmDialog
        open={confirmEnd}
        title="방학특강 종료"
        message={`“${activeTerm?.name ?? ''}” 방학특강을 종료할까요? 종료하면 출결이 다시 정규 시간표 기준으로 바뀝니다. 시간표는 저장되어 다음 방학특강 때 다시 사용할 수 있어요.`}
        confirmLabel="종료"
        loading={ending}
        onCancel={() => setConfirmEnd(false)}
        onConfirm={handleEnd}
      />

      <ConfirmDialog
        open={!!termToDelete}
        title="지난 방학특강 삭제"
        message={`“${termToDelete?.name ?? ''}” 기록을 완전히 삭제할까요? 이 시간표는 다시 사용할 수 없게 됩니다.`}
        confirmLabel="삭제"
        loading={deleting}
        onCancel={() => setTermToDelete(null)}
        onConfirm={handleDeleteTerm}
      />
    </div>
  );
}